import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, Lock, MessageSquare } from 'lucide-react';
import {
  CHAT_MESSAGE_NOTIFICATION_KEY,
  CHAT_TRANSFER_NOTIFICATION_KEY,
  desktopNotificationText,
  notificationEventKey,
  notificationTypeLabel,
  TICKET_EVENT_TO_NOTIFICATION_KEY,
  unreadBadge,
  type NotificationItem,
  type NotificationReply,
} from '@erp/shared';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { EmptyState } from '@/components/ui/misc';
import { useTransientToast } from '@/components/ui/transient-toast';
import { cn } from '@/lib/utils';
import {
  formatDateTime,
  notificationPrefsQueryOptions,
  profileQueryOptions,
} from '@/features/account/account-queries';
import { isPrefEnabled } from '@/features/account/notifications';
import { recentEventsQueryOptions } from '@/features/dashboard/dashboard-queries';
import {
  forgetLegacySeenAt,
  notificationRepliesQuery,
  notificationsBellQuery,
  NotificationsUnavailable,
  useNotificationMarks,
} from '@/features/notifications/notifications-queries';
import { subscribeRealtime, useRealtimeConnected } from '@/lib/realtime';
import { showDesktopNotification } from '@/shared/browser-notifications';
import {
  dismissChatAlert,
  isChatWindowOpen,
  lookupChatRequestorName,
  pushChatAlert,
  useChatAlerts,
  type ChatAlertEntry,
} from '@/shared/chat-notifications';
import { chatDetailOptions, chatListsKey } from '@/features/chats/chats-queries';
import { playNotificationDing } from '@/shared/notification-sound';
import { useTerms } from '@/shared/tenant-profile';

/** Título único do alerta de chat (toast, notificação do SO e linha do sino). */
function chatAlertTitle(entry: ChatAlertEntry): string {
  // spec 073 (P1): a conversa veio por transferência
  if (entry.transferredBy !== null) {
    const who = entry.requestorName ? ` com ${entry.requestorName}` : '';
    return `${entry.transferredBy} transferiu para você a conversa${who}`;
  }
  const extra = entry.count > 1 ? ` (${entry.count})` : '';
  return entry.requestorName !== null
    ? `Nova mensagem de ${entry.requestorName}${extra}`
    : `Nova mensagem no chat${extra}`;
}

/** id numérico do evento (a chave `e:<id>`) — a marca d'água dos alertas */
const eventIdOf = (item: NotificationItem): number => Number(item.key.slice(2)) || 0;

type BellTab = 'notificacoes' | 'respostas';

/**
 * Sino da topbar (spec 037): o contador e o lido vêm do SERVIDOR, até "99+";
 * as abas "Notificações (N)" e "Últimas respostas"; o rodapé com "Ver
 * notificações", "Marcar como lidas" e "Configurações". Clicar numa
 * notificação marca só ela como lida.
 */
export function NotificationsBell() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<BellTab>('notificacoes');
  const connected = useRealtimeConnected();
  const { toast, showToast } = useTransientToast('info');
  // Chat NÃO vem do feed do sino (não é evento de ticket): é a lista em memória
  // alimentada pelo próprio stream — ver chat-notifications.ts. É da sessão da
  // aba, e o "lido" dela também (some ao abrir a conversa).
  const chatAlerts = useChatAlerts();
  const [chatSeenAt, setChatSeenAt] = useState(0);
  const { readOne, readAll } = useNotificationMarks();
  const deskTerm = useTerms().desk;

  useEffect(() => forgetLegacySeenAt(), []);

  // O stream (ticket.event.created) invalida o feed e o refetch é imediato;
  // o poll vira só rede de segurança — 120s conectado, 60s sem stream.
  const bellQ = useQuery({ ...notificationsBellQuery(), refetchInterval: connected ? 120_000 : 60_000 });
  const prefsQ = useQuery(notificationPrefsQueryOptions());
  const profileQ = useQuery({ ...profileQueryOptions(), staleTime: 60_000 });
  const myId = profileQ.data?.id;

  // ── a API ainda sem /notifications (o SPA subiu antes): o feed antigo, só leitura
  const legacy = bellQ.error instanceof NotificationsUnavailable;
  const legacyQ = useQuery({
    ...recentEventsQueryOptions(),
    enabled: legacy,
    refetchInterval: legacy ? (connected ? 120_000 : 60_000) : false,
  });
  const items: NotificationItem[] = useMemo(() => {
    if (!legacy) return bellQ.data?.items ?? [];
    return (legacyQ.data ?? []).flatMap((event) => {
      const prefKey = TICKET_EVENT_TO_NOTIFICATION_KEY[event.type];
      if (prefKey === undefined || !isPrefEnabled(prefsQ.data, prefKey)) return [];
      if (myId !== undefined && event.userId === myId) return [];
      return [
        {
          key: notificationEventKey(event.id),
          kind: 'event' as const,
          ticketNumber: event.ticketNumber,
          ticketTitle: event.ticketTitle,
          deskName: '',
          eventType: event.type,
          prefKey,
          actorName: event.userName,
          createdAt: event.createdAt,
          read: true,
        },
      ];
    });
  }, [legacy, bellQ.data, legacyQ.data, prefsQ.data, myId]);

  const repliesQ = useQuery({ ...notificationRepliesQuery(), enabled: open && tab === 'respostas' && !legacy });

  const serverUnread = legacy ? 0 : (bellQ.data?.unread ?? 0);
  const chatUnread = chatAlerts.filter((entry) => entry.at > chatSeenAt).length;
  const unread = serverUnread + chatUnread;

  const openTicket = (ticketNumber: number) => {
    setOpen(false);
    void navigate({ to: '/tickets/$ticketNumber', params: { ticketNumber: String(ticketNumber) } });
  };
  const openItem = (item: NotificationItem) => {
    if (!item.read && !legacy) readOne.mutate(item.key);
    openTicket(item.ticketNumber);
  };
  const openChat = (chatId: number) => {
    setOpen(false);
    dismissChatAlert(chatId); // já foi atendida: sai da lista
    void navigate({ to: '/chats', search: { chat: chatId } });
  };
  const markAllRead = () => {
    setChatSeenAt(Date.now());
    if (legacy || serverUnread === 0) return;
    // até a mais nova que está na tela: a que chegar depois de abrir o sino
    // continua não lida
    const newest = items[0];
    readAll.mutate(newest ? new Date(newest.createdAt).toISOString() : undefined);
  };

  // ── Alerta (som + toast + notificação do SO) ────────────────────────────
  // Marca d'água pelo MAIOR id já visto neste page load: ids são a única chave
  // monotônica confiável (dois eventos no mesmo segundo empatam por createdAt).
  // A primeira carga NUNCA alerta — ela só arma a marca d'água, senão todo F5
  // tocaria o histórico. O feed do servidor já tirou o que a pessoa mesma fez e
  // os tipos desligados.
  const highWaterRef = useRef<number | null>(null);
  const openRef = useRef(open);
  openRef.current = open;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const prefsReady = !prefsQ.isPending;
  const soundOn = prefsQ.data?.soundEnabled !== false; // ausente = ligado
  const browserOn = prefsQ.data?.browserEnabled === true; // opt-in explícito
  const feedReady = legacy ? legacyQ.isSuccess : bellQ.isSuccess;

  useEffect(() => {
    if (!feedReady) return;
    const maxId = items.reduce((max, i) => Math.max(max, eventIdOf(i)), 0);
    const previous = highWaterRef.current;
    if (previous === null) {
      highWaterRef.current = maxId; // primeira carga: arma e cala
      return;
    }
    if (maxId <= previous) return;
    highWaterRef.current = maxId;
    if (!prefsReady) return; // sem prefs resolvidas não dá para respeitar mudo

    const fresh = items.filter((i) => eventIdOf(i) > previous && !i.read);
    // Popover aberto = a pessoa JÁ está olhando o feed: o contador atualiza,
    // mas som/toast/notificação ficam quietos.
    if (openRef.current) return;
    const newest = fresh[0]; // o feed vem do mais recente para o mais antigo
    if (!newest) return;
    const text = desktopNotificationText(newest, fresh.length - 1, deskTerm);

    if (soundOn) playNotificationDing(); // debounce de 3s mora no módulo
    showToast(`#${newest.ticketNumber} — ${text.title}`);
    if (browserOn) {
      showDesktopNotification({
        title: text.title,
        body: text.body,
        tag: `erp-ticket-${newest.ticketNumber}`,
        onClick: () =>
          void navigateRef.current({
            to: '/tickets/$ticketNumber',
            params: { ticketNumber: String(newest.ticketNumber) },
          }),
      });
    }
    // `open` fica FORA das deps de propósito (lido por ref): abrir/fechar o
    // popover não é motivo para reavaliar alertas.
  }, [items, feedReady, prefsReady, soundOn, browserOn, deskTerm, showToast]);

  // ── Alerta de CHAT (mensagem nova do cliente) ───────────────────────────
  // Vem do STREAM, não de um feed: `chat.message.created` já diz o lado da
  // mensagem (direction). As supressões espelham as do ticket:
  //  • direction !== 'inbound' ⇒ nunca alerta (a resposta da equipe, o bot, o
  //    sistema e o evento sem o campo, de réplica antiga);
  //  • janela daquele chat aberta ⇒ a pessoa está lendo (nem lista, nem som);
  //  • categoria desligada nas preferências;
  //  • popover aberto ⇒ a linha entra na lista, mas sem som/toast/SO.
  const chatOn = isPrefEnabled(prefsQ.data, CHAT_MESSAGE_NOTIFICATION_KEY);
  useEffect(() => {
    if (!prefsReady || !chatOn) return;
    return subscribeRealtime((event) => {
      if (event.type !== 'chat.message.created' || event.direction !== 'inbound') return;
      if (isChatWindowOpen(qc, event.chatId)) return;
      const name = lookupChatRequestorName(qc, event.chatId);
      const entry = pushChatAlert({ chatId: event.chatId, requestorName: name });
      if (openRef.current) return;
      const title = chatAlertTitle(entry);
      if (soundOn) playNotificationDing();
      showToast(title);
      if (browserOn) {
        showDesktopNotification({
          title,
          body: entry.count > 1 ? `${entry.count} mensagens novas` : 'Abra para responder',
          // uma vaga por chat: mensagens seguintes substituem a notificação
          tag: `erp-chat-${event.chatId}`,
          onClick: () => {
            dismissChatAlert(event.chatId);
            void navigateRef.current({ to: '/chats', search: { chat: event.chatId } });
          },
        });
      }
    });
  }, [qc, prefsReady, chatOn, soundOn, browserOn, showToast]);

  // ── Conversa transferida para mim (spec 073, P1) ───────────────────────
  // O evento vai ao tenant inteiro; só a aba de quem RECEBE reage. A pessoa
  // pode não ter a conversa no cache (é de outra mesa): busca o detalhe, que
  // ela enxerga por ser a responsável, só para o nome do cliente.
  const transferOn = isPrefEnabled(prefsQ.data, CHAT_TRANSFER_NOTIFICATION_KEY);
  useEffect(() => {
    if (!prefsReady || !transferOn || myId === undefined) return;
    return subscribeRealtime((event) => {
      if (event.type !== 'chat.transferred' || event.toUserId !== myId) return;
      void qc.invalidateQueries({ queryKey: chatListsKey });
      void (async () => {
        let name = lookupChatRequestorName(qc, event.chatId);
        if (name === null) {
          name = await qc
            .fetchQuery(chatDetailOptions(event.chatId))
            .then((d) => d.requestorName || null)
            .catch(() => null);
        }
        const entry = pushChatAlert({ chatId: event.chatId, requestorName: name, transferredBy: event.byName });
        if (openRef.current) return;
        const title = chatAlertTitle(entry);
        if (soundOn) playNotificationDing();
        showToast(title);
        if (browserOn) {
          showDesktopNotification({
            title,
            body: 'Abra para responder',
            tag: `erp-chat-${event.chatId}`,
            onClick: () => {
              dismissChatAlert(event.chatId);
              void navigateRef.current({ to: '/chats', search: { chat: event.chatId } });
            },
          });
        }
      })();
    });
  }, [qc, prefsReady, transferOn, myId, soundOn, browserOn, showToast]);

  // ── Conversa tirada da minha mão (spec 013) ─────────────────────────────
  useEffect(() => {
    if (myId === undefined) return;
    return subscribeRealtime((event) => {
      if (event.type !== 'chat.taken_over' || event.previousResponsibleId !== myId) return;
      const name = lookupChatRequestorName(qc, event.chatId);
      showToast(`Outro atendente assumiu sua conversa${name ? ` com ${name}` : ''}.`);
    });
  }, [qc, myId, showToast]);

  const tabButton = (key: BellTab, label: string) => (
    <button
      type="button"
      role="tab"
      aria-selected={tab === key}
      onClick={() => setTab(key)}
      className={cn(
        'relative flex h-10 items-center px-1 text-sm font-medium transition-colors',
        tab === key
          ? 'text-primary after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-primary'
          : 'text-muted-foreground hover:text-foreground'
      )}
    >
      {label}
    </button>
  );

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-tour="sino"
            aria-label={unread > 0 ? `Notificações (${unreadBadge(unread)} não lidas)` : 'Notificações'}
            className="relative flex size-8 items-center justify-center rounded-full text-muted-foreground outline-none transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50"
          >
            <Bell className="size-[18px]" />
            {unread > 0 ? (
              <span
                aria-hidden
                className="absolute -top-0.5 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] leading-none font-semibold text-white"
              >
                {unreadBadge(unread)}
              </span>
            ) : null}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-96 p-0">
          <div role="tablist" className="flex gap-5 border-b border-divider px-3">
            {tabButton('notificacoes', `Notificações (${unreadBadge(unread)})`)}
            {tabButton('respostas', 'Últimas respostas')}
          </div>

          {tab === 'notificacoes' ? (
            <div role="tabpanel">
              {/* Mensagens de chat desta sessão da aba (não persistem — o que
                  persiste é o badge de não lidas do menu Chats). */}
              {chatAlerts.length > 0 ? (
                <ul className="max-h-40 overflow-y-auto border-b border-divider py-1">
                  {chatAlerts.map((entry) => (
                    <li key={entry.chatId}>
                      <button
                        type="button"
                        onClick={() => openChat(entry.chatId)}
                        className="flex w-full flex-col gap-0.5 px-3 py-2 text-left transition-colors hover:bg-accent"
                      >
                        <span className="flex items-center gap-2 text-xs text-muted-foreground">
                          {formatDateTime(new Date(entry.at))}
                          {entry.at > chatSeenAt ? (
                            <span aria-label="não lida" className="size-1.5 rounded-full bg-primary" />
                          ) : null}
                        </span>
                        <span className="flex items-center gap-1.5 text-sm">
                          <MessageSquare aria-hidden className="size-3.5 shrink-0 text-primary" />
                          <span className="truncate text-foreground">{chatAlertTitle(entry)}</span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}

              {items.length === 0 && chatAlerts.length === 0 ? (
                <EmptyState
                  message={bellQ.isPending ? 'Carregando…' : bellQ.isError && !legacy ? 'Erro ao carregar as notificações' : 'Nenhuma notificação'}
                  className="h-32"
                />
              ) : null}

              {items.length > 0 ? (
                <ul className="max-h-80 overflow-y-auto py-1">
                  {items.map((item) => (
                    <BellRow key={item.key} item={item} onOpen={() => openItem(item)} />
                  ))}
                </ul>
              ) : null}
              {legacy ? (
                <p role="status" className="border-t border-divider px-3 py-2 text-[12px] text-muted-foreground">
                  As notificações estão sendo atualizadas: o contador volta em alguns minutos.
                </p>
              ) : null}
            </div>
          ) : (
            <div role="tabpanel">
              <RepliesList
                replies={repliesQ.data?.items ?? []}
                state={legacy ? 'unavailable' : repliesQ.isPending ? 'loading' : repliesQ.isError ? 'error' : 'ready'}
                onOpen={(reply) => openTicket(reply.ticketNumber)}
              />
            </div>
          )}

          <div className="grid grid-cols-3 border-t border-divider text-sm font-medium">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                void navigate({ to: '/notificacoes' });
              }}
              className="flex h-9 items-center justify-center text-primary transition-colors hover:bg-accent"
            >
              Ver notificações
            </button>
            <button
              type="button"
              onClick={markAllRead}
              disabled={unread === 0 || readAll.isPending}
              className="flex h-9 items-center justify-center border-x border-divider text-primary transition-colors hover:bg-accent disabled:cursor-default disabled:text-muted-foreground/60"
            >
              Marcar como lidas
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                void navigate({ to: '/account', search: { tab: 'notificacoes' } });
              }}
              className="flex h-9 items-center justify-center text-primary transition-colors hover:bg-accent"
            >
              Configurações
            </button>
          </div>
        </PopoverContent>
      </Popover>
      {toast}
    </>
  );
}

/** Uma notificação: quando, o tipo, o ticket, a mesa e quem fez. */
export function BellRow({ item, onOpen }: { item: NotificationItem; onOpen: () => void }) {
  const who = [item.deskName, item.actorName].filter(Boolean).join(' · ');
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className={cn(
          'flex w-full flex-col gap-0.5 px-3 py-2 text-left transition-colors hover:bg-accent',
          !item.read && 'bg-primary/5'
        )}
      >
        <span className="flex items-center gap-2 text-xs text-muted-foreground">
          {formatDateTime(item.createdAt)}
          {!item.read ? <span aria-label="não lida" className="size-1.5 rounded-full bg-primary" /> : null}
        </span>
        <span className="flex items-center gap-1 text-sm">
          <span className="font-bold text-primary">#{item.ticketNumber}</span>
          <span className="text-foreground">— {notificationTypeLabel(item)}</span>
          {item.internal ? <Lock aria-label="interna" className="size-3 text-amber-600" /> : null}
        </span>
        <span className="truncate text-[13px] text-muted-foreground">{item.ticketTitle}</span>
        {who ? <span className="truncate text-[12px] text-muted-foreground/80">{who}</span> : null}
      </button>
    </li>
  );
}

function RepliesList({
  replies,
  state,
  onOpen,
}: {
  replies: NotificationReply[];
  state: 'loading' | 'error' | 'ready' | 'unavailable';
  onOpen: (reply: NotificationReply) => void;
}) {
  if (state !== 'ready' || replies.length === 0) {
    const message =
      state === 'loading'
        ? 'Carregando…'
        : state === 'error'
          ? 'Erro ao carregar as respostas'
          : state === 'unavailable'
            ? 'Disponível em alguns minutos'
            : 'Nenhuma resposta nos seus tickets';
    return <EmptyState message={message} className="h-32" />;
  }
  return (
    <ul className="max-h-96 overflow-y-auto py-1" aria-label="Comunicações recentes nos tickets em que você é o responsável">
      {replies.map((reply) => (
        <li key={reply.commentId}>
          <button
            type="button"
            onClick={() => onOpen(reply)}
            className="flex w-full flex-col gap-0.5 px-3 py-2 text-left transition-colors hover:bg-accent"
          >
            <span className="flex items-center gap-2 text-xs text-muted-foreground">
              {formatDateTime(reply.createdAt)}
              {reply.internal ? (
                <span className="flex items-center gap-0.5 text-amber-700">
                  <Lock aria-hidden className="size-3" />
                  interna
                </span>
              ) : null}
            </span>
            <span className="text-sm">
              <span className="font-bold text-primary">#{reply.ticketNumber}</span>
              <span className="text-foreground"> — {reply.authorName}</span>
            </span>
            <span className="line-clamp-2 text-[13px] text-muted-foreground">{reply.excerpt}</span>
            <span className="truncate text-[12px] text-muted-foreground/80">
              {reply.ticketTitle} · {reply.deskName}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}
